import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { PlanPromptService } from '../../../../services/plan-prompt.service';

type HeroPhase =
  | 'idle'
  | 'ringing'
  | 'fading-to-inform'
  | 'inform-active'
  | 'fading-to-branded'
  | 'branded-active'
  | 'fading-out';

type VisibleImage = 'unknown' | 'inform' | 'branded';

const IDLE_MS = 1800;
const RING_MS = 1800;
const FADE_MS = 1100;
const INFORM_HOLD_MS = 2400;
const BRANDED_HOLD_MS = 2800;

@Component({
  selector: 'app-home-banner',
  standalone: false,
  templateUrl: './home-banner.component.html',
  styleUrl: './home-banner.component.scss',
})
export class HomeBannerComponent implements OnInit, OnDestroy {
  planPrompt = inject(PlanPromptService);

  phase: HeroPhase = 'idle';
  visibleImage: VisibleImage = 'unknown';

  private timeouts: Array<ReturnType<typeof setTimeout>> = [];

  ngOnInit(): void {
    this.runCycle();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  get isRinging(): boolean {
    return this.phase === 'ringing';
  }

  get isInformActive(): boolean {
    return this.phase === 'inform-active' || this.phase === 'fading-to-inform';
  }

  get isBrandedActive(): boolean {
    return this.phase === 'branded-active' || this.phase === 'fading-to-branded';
  }

  get labelText(): string {
    if (this.isBrandedActive) return 'Enhanced Branded Calling';
    if (this.isInformActive) return 'INFORM Call Active';
    if (this.isRinging) return 'Incoming Call…';
    return 'Without Branded Calling';
  }

  get dotColor(): string {
    if (this.isBrandedActive) return '#16A34A';
    if (this.isInformActive) return '#22C55E';
    if (this.isRinging) return '#EF4444';
    return '#94A3B8';
  }

  get labelColor(): string {
    if (this.isBrandedActive) return '#15803D';
    if (this.isInformActive) return '#16A34A';
    if (this.isRinging) return '#DC2626';
    return '#64748B';
  }

  get pillBg(): string {
    if (this.isBrandedActive) return 'rgba(22,163,74,0.08)';
    if (this.isInformActive) return 'rgba(34,197,94,0.07)';
    if (this.isRinging) return 'rgba(239,68,68,0.07)';
    return 'rgba(7,29,73,0.05)';
  }

  get pillBorder(): string {
    if (this.isBrandedActive) return 'rgba(22,163,74,0.25)';
    if (this.isInformActive) return 'rgba(34,197,94,0.2)';
    if (this.isRinging) return 'rgba(239,68,68,0.2)';
    return 'rgba(7,29,73,0.08)';
  }

  scrollToPricing(event: Event): void {
    event.preventDefault();
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  }

  private clearTimers(): void {
    for (const t of this.timeouts) {
      clearTimeout(t);
    }
    this.timeouts = [];
  }

  private schedule(fn: () => void, ms: number): void {
    this.timeouts.push(setTimeout(fn, ms));
  }

  private runCycle(): void {
    this.clearTimers();

    this.phase = 'idle';
    this.visibleImage = 'unknown';

    this.schedule(() => {
      this.phase = 'ringing';

      this.schedule(() => {
        this.phase = 'fading-to-inform';
        this.visibleImage = 'inform';

        this.schedule(() => {
          this.phase = 'inform-active';

          this.schedule(() => {
            this.phase = 'fading-to-branded';
            this.visibleImage = 'branded';

            this.schedule(() => {
              this.phase = 'branded-active';

              this.schedule(() => {
                this.phase = 'fading-out';
                this.visibleImage = 'unknown';

                this.schedule(() => this.runCycle(), FADE_MS);
              }, BRANDED_HOLD_MS);
            }, FADE_MS);
          }, INFORM_HOLD_MS);
        }, FADE_MS);
      }, RING_MS);
    }, IDLE_MS);
  }
}
